import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import type { PaytrieAPI } from '@paytrie/client'

import { getDurationMs, getWindowStatus } from '../maintenanceTimeline'
import type { MaintenanceFormatters } from '../useMaintenanceFormatters'
import {
  CARD_CLASS_NAME,
  MAINTENANCE_TITLE_CLASS_NAME,
  MUTED_TEXT_CLASS_NAME,
} from './styles'

type MaintenanceNoticeProps = {
  window: PaytrieAPI.MaintenanceWindow
  now: Date
  formatters: MaintenanceFormatters
}

export const MaintenanceNotice = memo(function MaintenanceNotice({
  window,
  now,
  formatters,
}: MaintenanceNoticeProps) {
  const { t } = useTranslation()
  const status = getWindowStatus({ window, now })
  const isInProgress = status === 'in-progress'
  const timing = [
    status === 'scheduled'
      ? t('maintenance.window.startsIn', {
          relative: formatters.formatRelative({ date: window.startsAt, now }),
        })
      : null,
    window.endsAt
      ? formatters.formatDuration(
          getDurationMs({ startsAt: window.startsAt, endsAt: window.endsAt })
        )
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <article
      data-testid="maintenance-notice"
      className={clsx(
        CARD_CLASS_NAME,
        'overflow-hidden',
        isInProgress && 'border-sky-600 dark:border-sky-500'
      )}
    >
      <h3
        className={clsx(
          'px-5 py-3',
          isInProgress
            ? 'bg-sky-600 font-semibold text-white'
            : clsx(
                MAINTENANCE_TITLE_CLASS_NAME,
                'border-b border-cloudDarker dark:border-darkCharcoal'
              )
        )}
      >
        {window.title}
      </h3>
      <div className="space-y-3 px-5 py-4 text-sm">
        <div>
          <p className="font-medium text-gravel dark:text-snow">
            {formatters.formatSchedule(window)}
          </p>
          {timing && (
            <p className={clsx('mt-0.5', MUTED_TEXT_CLASS_NAME)}>{timing}</p>
          )}
        </div>
        <p className="leading-relaxed text-gravel/85 dark:text-semiWhite">
          {window.description}
        </p>
      </div>
    </article>
  )
})
